import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Cell Structure and Function",
  examWeight: "10–13%",
  focus: "Evolution (1), Energetics (2), Systems Interactions (4)",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Cell Structure: Subcellular Components",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔬 Subcellular Components</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Cells are the basic structural and functional units of life. The structure of a specific organelle directly facilitates its function (<strong>structure determines function</strong>).
            </p>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🧬 1. Ribosomes</h4>
                <p className="text-sm text-slate-700 mt-1">Consist of two subunits (large and small) made of <strong>ribosomal RNA (rRNA)</strong> and proteins. Non-membrane-bound.</p>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                  <li><strong>Free Ribosomes:</strong> Floating in cytosol; produce proteins for use <em>within</em> the cell (e.g., glycolysis enzymes).</li>
                  <li><strong>Bound Ribosomes:</strong> Attached to Rough ER; produce proteins for <strong>secretion</strong>, membrane incorporation, or lysosomes.</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-slate-50 to-gray-100 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">📦 2. Endoplasmic Reticulum (ER)</h4>
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  <div>
                    <h5 className="font-bold text-xs uppercase text-slate-500">Rough ER</h5>
                    <p className="text-sm text-slate-700">Has ribosomes attached. Compartmentalizes the cell and packages newly synthesized proteins for export.</p>
                  </div>
                  <div>
                    <h5 className="font-bold text-xs uppercase text-slate-500">Smooth ER</h5>
                    <p className="text-sm text-slate-700">Lacks ribosomes. Functions in <strong>lipid synthesis</strong> (steroids/phospholipids) and detoxification of poisons.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🏬 3. The Golgi Complex</h4>
                <p className="text-sm text-slate-700 mt-1">Series of flattened membrane sacs called <strong>cisternae</strong>. Involved in folding and chemical modification (e.g., glycosylation) of proteins.</p>
                <p className="text-xs text-slate-600 mt-2 italic">Vesicles enter the <strong>cis face</strong> and leave via the <strong>trans face</strong>.</p>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🔋 4. Mitochondria</h4>
                <p className="text-sm text-slate-700 mt-1">Site of <strong>Aerobic Cellular Respiration</strong> (ATP production). Double membrane structure.</p>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                  <li><strong>Cristae:</strong> Folded inner membrane that increases <strong>surface area</strong> for the ETC and ATP synthase.</li>
                  <li><strong>Matrix:</strong> Fluid-filled center where the Krebs Cycle occurs.</li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                  <h4 className="font-bold text-red-900">⚗️ 5. Lysosomes</h4>
                  <p className="text-sm text-slate-700 mt-1">Contain <strong>hydrolytic enzymes</strong> for intracellular digestion, recycling organic materials (<strong>autophagy</strong>), and programmed cell death (<strong>apoptosis</strong>).</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <h4 className="font-bold text-blue-900">🕳️ 6. Vacuoles</h4>
                  <p className="text-sm text-slate-700 mt-1">Storage for macromolecules/waste. The <strong>Central Vacuole</strong> in plants aids in <strong>turgor pressure</strong> to keep the plant upright.</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🍃 7. Chloroplasts</h4>
                <p className="text-sm text-slate-700 mt-1">Found in algae/plants. Double outer membrane. Site of <strong>Photosynthesis</strong>.</p>
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  <p className="text-xs text-slate-600"><strong>Thylakoids:</strong> Stacks (grana) containing chlorophyll for light-dependent reactions.</p>
                  <p className="text-xs text-slate-600"><strong>Stroma:</strong> Fluid surrounding thylakoids; site of the Calvin Cycle (Carbon Fixation).</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Cell Size",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Surface Area-to-Volume Ratio (SA:V)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The efficiency of material exchange is dictated by cell size. Cells must move nutrients in and waste out across the plasma membrane.
            </p>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Volume (<InlineMath math="r^3" />):</strong> Determines the demand for resources and waste production.</li>
                <li><strong>Surface Area (<InlineMath math="r^2" />):</strong> Determines the rate of exchange.</li>
                <li><strong>Conclusion:</strong> Higher SA:V ratios are more efficient. Smaller cells have higher SA:V ratios.</li>
              </ul>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              If a cell gets too large, it cannot move materials fast enough to support its volume, triggering division or death.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Structural Adaptations</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-white p-3 rounded border border-slate-200">
                <p className="font-bold text-slate-800">Root Hairs</p>
                <p className="text-xs text-slate-600">Extensions of epidermal cells that increase SA for water absorption.</p>
              </div>
              <div className="bg-white p-3 rounded border border-slate-200">
                <p className="font-bold text-slate-800">Villi/Microvilli</p>
                <p className="text-xs text-slate-600">Finger-like projections in the small intestine for nutrient absorption.</p>
              </div>
              <div className="bg-white p-3 rounded border border-slate-200 md:col-span-2">
                <p className="font-bold text-slate-800">Flattened Shapes</p>
                <p className="text-xs text-slate-600">Elephant ears (heat dissipation) or flatworms (gas exchange directly through skin).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Cell Structure and Function (Plasma Membrane)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧱 Membrane Composition</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Phospholipids (Amphipathic)</h4>
                <p className="text-sm text-slate-700 mt-1">Molecules with both hydrophilic and hydrophobic regions.</p>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-600">
                  <li><strong>Head:</strong> Hydrophilic, polar, phosphate group. Faces aqueous environment.</li>
                  <li><strong>Tail:</strong> Hydrophobic, nonpolar, fatty acids. Faces inward.</li>
                </ul>
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">2. Embedded Proteins</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-2">
                  <li><strong>Peripheral Proteins:</strong> Loosely bound to surface; hydrophilic.</li>
                  <li><strong>Integral Proteins:</strong> Span the membrane; have hydrophobic regions interacting with tails.</li>
                  <li><strong>Functions:</strong> Transport, signaling, cell-cell recognition, enzymatic activity.</li>
                </ul>
              </div>

              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">3. Fluid Mosaic Model</h4>
                <p className="text-sm text-slate-700 mt-1">A mosaic of macromolecules (lipids, proteins, carbs) that can shift laterally.</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Cholesterol:</strong> Acts as a "fluidity buffer"—keeps membrane fluid at low temps and stable at high temps.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Membrane Permeability",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚪 Selective Permeability</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Direct Passage ✅</h4>
                <p className="text-xs text-slate-600 mb-2">Small, Nonpolar Molecules</p>
                <ul className="list-disc pl-5 text-sm text-slate-700">
                  <li><InlineMath math="N_2, O_2, CO_2" /></li>
                  <li>Pass freely through the hydrophobic core.</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Regulated Passage ❌</h4>
                <p className="text-xs text-slate-600 mb-2">Large Polar Molecules & Ions</p>
                <ul className="list-disc pl-5 text-sm text-slate-700">
                  <li>Glucose, <InlineMath math="Na^+, K^+, Cl^-" /></li>
                  <li>Require transport proteins.</li>
                </ul>
              </div>
            </div>
            <div className="mt-4">
              <KeyTerm 
                term="Cell Walls" 
                definition="Found in plants (cellulose), fungi (chitin), and prokaryotes (peptidoglycan). Provide structural boundary and permeability barrier."
              />
              <p className="text-xs text-slate-500 mt-2"><strong>Plasmodesmata:</strong> Holes in plant cell walls for cytosol connection.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Membrane Transport",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Moving Matter</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">1. Passive Transport</h4>
                <p className="text-sm text-slate-700">Movement from <strong>High → Low</strong> concentration. No ATP required.</p>
                <p className="text-xs text-slate-600 mt-1">Includes simple diffusion and facilitated diffusion.</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">2. Active Transport</h4>
                <p className="text-sm text-slate-700">Movement from <strong>Low → High</strong> concentration. <strong>Requires metabolic energy (ATP)</strong> and carrier proteins.</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">3. Bulk Transport (Active)</h4>
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  <div>
                    <h5 className="font-bold text-xs uppercase">Endocytosis</h5>
                    <p className="text-xs text-slate-700">Taking in material via vesicles. Phagocytosis (eating) and Pinocytosis (drinking).</p>
                  </div>
                  <div>
                    <h5 className="font-bold text-xs uppercase">Exocytosis</h5>
                    <p className="text-xs text-slate-700">Vesicles fuse with membrane to release contents (e.g., hormones, neurotransmitters).</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Facilitated Diffusion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎟️ Protein-Aided Diffusion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">For molecules that cannot cross the lipid bilayer alone. Passive transport.</p>
            <div className="space-y-3">
              <KeyTerm 
                term="Aquaporins" 
                definition="Specialized channel proteins that allow massive quantities of water to pass through the membrane."
              />
              <KeyTerm 
                term="Ion Channels" 
                definition="Gated tunnels that open/close in response to stimuli (voltage/ligand) to let ions through."
              />
              <KeyTerm 
                term="Carrier Proteins" 
                definition="Bind to specific molecules (e.g., glucose) and change shape to shuttle them across."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Tonicity and Osmoregulation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💧 Water Balance</h3>
            <p className="text-slate-700 mb-4"><strong>Osmosis:</strong> Diffusion of free water from <strong>High Water Potential → Low Water Potential</strong>.</p>
            
            <div className="bg-slate-100 p-4 rounded-lg border border-slate-300 mb-6">
              <h4 className="font-bold text-slate-900 mb-2 text-center">Tonicity Relative to the Cell</h4>
              <div className="grid md:grid-cols-3 gap-2">
                <div className="bg-white p-2 rounded text-center border">
                  <p className="font-bold text-xs text-red-600">Hypertonic</p>
                  <p className="text-[10px]">Higher solute outside. Water leaves; cell shrivels/plasmolyzes.</p>
                </div>
                <div className="bg-white p-2 rounded text-center border">
                  <p className="font-bold text-xs text-green-600">Hypotonic</p>
                  <p className="text-[10px]">Lower solute outside. Water enters; cell swells (turgid) or bursts (lyse).</p>
                </div>
                <div className="bg-white p-2 rounded text-center border">
                  <p className="font-bold text-xs text-blue-600">Isotonic</p>
                  <p className="text-[10px]">Equal solute. No net movement. Dynamic equilibrium.</p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Water Potential (<InlineMath math="\Psi" />)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="\Psi = \Psi_p + \Psi_s" />
              <div className="grid md:grid-cols-2 gap-4 mt-4 border-t border-blue-100 pt-4">
                <div>
                  <h5 className="font-bold text-blue-900 text-sm">Pressure Potential (<InlineMath math="\Psi_p" />)</h5>
                  <p className="text-xs text-slate-700">Physical pressure. Open beaker = 0.</p>
                </div>
                <div>
                  <h5 className="font-bold text-blue-900 text-sm">Solute Potential (<InlineMath math="\Psi_s" />)</h5>
                  <BlockMath math="\Psi_s = -iCRT" />
                  <ul className="text-[10px] text-slate-600 list-none mt-1">
                    <li><InlineMath math="i" /> = Ionization constant (1 for sucrose, 2 for NaCl)</li>
                    <li><InlineMath math="C" /> = Molar concentration</li>
                    <li><InlineMath math="R" /> = Pressure constant (0.0831)</li>
                    <li><InlineMath math="T" /> = Temperature in Kelvin (<InlineMath math="273 + ^\circ C" />)</li>
                  </ul>
                </div>
              </div>
            </div>
            <ImportantNote>
              Adding solute <strong>lowers</strong> water potential (makes it more negative). Water always moves toward the more negative number.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Mechanisms of Transport",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Gradients and Pumps</h3>
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">The Sodium-Potassium Pump (<InlineMath math="Na^+/K^+" /> ATPase)</h4>
                <p className="text-sm text-slate-700 mt-1">Found in animal cells. Pumps <strong>3 <InlineMath math="Na^+" /> OUT</strong> and <strong>2 <InlineMath math="K^+" /> IN</strong> per ATP. Contributes to membrane potential (negative inside).</p>
              </div>

              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Proton Pumps & Cotransport</h4>
                <p className="text-sm text-slate-700 mt-1">Plants/fungi pump <InlineMath math="H^+" /> out. The resulting gradient is used to do work (Cotransport). As <InlineMath math="H^+" /> diffuses back <em>in</em>, it brings <strong>sucrose</strong> with it against its own gradient.</p>
              </div>
              
              <KeyTerm 
                term="Electrochemical Gradient" 
                definition="The combined force of the concentration gradient (chemical) and the membrane potential (electrical) driving ion movement."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: Cell Compartmentalization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 Internal Organization</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Eukaryotic Cells</h4>
                <p className="text-sm text-slate-700 mt-2">Internal membranes partition the cell into specialized regions.</p>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                  <li><strong>Minimizes Competing Interactions:</strong> Lysosome (pH 5) vs Cytosol (pH 7).</li>
                  <li><strong>Increases Surface Area:</strong> Folds in ER/Mitochondria facilitate metabolic processes.</li>
                </ul>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Prokaryotic Cells</h4>
                <p className="text-sm text-slate-700 mt-2">Lack membrane-bound organelles. Specialized functions occur in the <strong>nucleoid</strong> region or on the plasma membrane.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-10",
      title: "Topic 2.10: Origins of Cell Compartmentalization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Endosymbiotic Theory</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Theory that an early eukaryotic ancestor engulfed an oxygen-using non-photosynthetic prokaryote, which eventually became the mitochondrion (and later chloroplasts for plants).
            </p>
            <div className="bg-green-50 p-5 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900 mb-3">Evidence for Endosymbiosis (Similar to Bacteria)</h4>
              <ul className="space-y-3">
                <li className="flex gap-2 text-sm text-slate-700">
                  <span className="font-bold text-green-800">1. Membranes:</span> Double membranes (inner matches prokaryote; outer matches host).
                </li>
                <li className="flex gap-2 text-sm text-slate-700">
                  <span className="font-bold text-green-800">2. DNA:</span> Own <strong>circular DNA</strong>, separate from linear nuclear DNA.
                </li>
                <li className="flex gap-2 text-sm text-slate-700">
                  <span className="font-bold text-green-800">3. Ribosomes:</span> Contain <strong>70S ribosomes</strong> (size of bacterial ribosomes).
                </li>
                <li className="flex gap-2 text-sm text-slate-700">
                  <span className="font-bold text-green-800">4. Reproduction:</span> Reproduce independently via <strong>binary fission</strong>-like processes.
                </li>
              </ul>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit2Notes;